/*
CodeMirror 6 editor factory — the "heavy" module.

`code-editor.ts` imports this **dynamically** (on first `<tosi-code>` connect), so
CodeMirror only enters a consumer's bundle as a separate lazy chunk when the editor
is actually used — mirroring the way the old ACE build loaded from a CDN on first
use. No `<tosi-code>` in the page → no CodeMirror.

Languages here are the always-available set (js/ts/css/html/markdown). First-class
tjs (highlighting + runtime-value autocomplete via tjs-lang's editors/codemirror) is
layered in separately and only when tjs-lang is installed.
*/

import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLineGutter,
  highlightSpecialChars,
  drawSelection,
  dropCursor,
  rectangularSelection,
  crosshairCursor,
  highlightActiveLine,
  tooltips,
} from '@codemirror/view'
import {
  EditorState,
  Compartment,
  Extension,
  Annotation,
  Transaction,
} from '@codemirror/state'

import {
  indentWithTab,
  history,
  defaultKeymap,
  historyKeymap,
  undo,
  redo,
  undoDepth,
  redoDepth,
} from '@codemirror/commands'
import {
  indentUnit,
  foldGutter,
  foldKeymap,
  indentOnInput,
  syntaxHighlighting,
  defaultHighlightStyle,
  HighlightStyle,
  bracketMatching,
} from '@codemirror/language'
import {
  closeBrackets,
  closeBracketsKeymap,
  autocompletion,
  completionKeymap,
} from '@codemirror/autocomplete'
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search'
import { lintKeymap } from '@codemirror/lint'
import { javascript } from '@codemirror/lang-javascript'
import { css } from '@codemirror/lang-css'
import { html } from '@codemirror/lang-html'
import { markdown } from '@codemirror/lang-markdown'
import { oneDarkHighlightStyle } from '@codemirror/theme-one-dark'

// The doc-system flips dark mode with a `darkmode` class on <body> (which inverts
// the luminance of --code-bg / --text-color, so the editor SURFACE adapts for
// free). Only CM's syntax-token colors need swapping: the light defaultHighlightStyle
// washes out on a dark surface, so use oneDark's brighter palette in dark mode.
const isDarkMode = (): boolean =>
  typeof document !== 'undefined' &&
  !!document.body &&
  document.body.classList.contains('darkmode')

/**
 * Marks a transaction as OURS — a programmatic `value` set, not a user edit.
 *
 * `change` is documented as "fires when the text changes", and consumers write
 * `code.addEventListener('change', e => save(e.detail.value))`. Without this, `el.value
 * = doc` — merely LOADING a document — dispatches a CM transaction, `docChanged` is
 * true, and the app records a spurious save/dirty-flag every time it populates the
 * editor. A `disabled` (read-only) editor fired it too: `EditorState.readOnly` gates
 * user input, not `view.dispatch`. The library itself does this in five places,
 * including writing results into a read-only *output* editor after every example run.
 *
 * The event is new in 1.7 (the ACE build dispatched nothing), so there is no prior
 * semantic to preserve — and tightening it after 1.7 is on `latest` would be breaking.
 */
const programmatic = Annotation.define<boolean>()

/*
CodeMirror's light style, with its one colour under WCAG AA's 4.5:1 darkened (#3572): type and
namespace names are #085, 4.43:1 on a near-white editor. #075 is 5.5:1 and reads the same.
*/
const AA_COLORS: Record<string, string> = { '#085': '#075' }
const lightHighlightStyle = HighlightStyle.define(
  defaultHighlightStyle.specs.map((spec) =>
    typeof spec.color === 'string' && AA_COLORS[spec.color]
      ? { ...spec, color: AA_COLORS[spec.color] }
      : spec
  )
)

const highlightFor = (dark: boolean): Extension =>
  dark
    ? syntaxHighlighting(oneDarkHighlightStyle)
    : syntaxHighlighting(lightHighlightStyle, { fallback: true })

/** Map a `<tosi-code mode>` value to a CodeMirror language extension. */
export function languageForMode(mode: string): Extension {
  switch (mode) {
    case 'js':
    case 'javascript':
      return javascript()
    case 'ts':
    case 'typescript':
    // tjs highlights as TypeScript here (the sync default). First-class tjs — the
    // tjs-lang language + autocomplete — is layered on asynchronously via
    // `loadTjsExtension()` + `setLanguageExtension()`, since it lazy-loads the
    // (optional) tjs-lang editor build; this keeps `mode="tjs"` readable meanwhile.
    // falls through
    case 'tjs':
    case 'ajs':
      return javascript({ typescript: true })
    case 'css':
      return css()
    case 'html':
      return html()
    case 'md':
    case 'markdown':
      return markdown()
    default:
      return javascript()
  }
}

// The language compartment carries the language AND its completion source, so
// exactly ONE `autocompletion()` is ever active: standard modes use CM's built-in
// (language-provided) completions here, while the tjs bundle brings its own
// `autocompletion({ override: [tjsCompletionSource] })`. (This is why the base
// extension set below does NOT include `autocompletion()`.)
function standardLanguageBundle(mode: string): Extension {
  return [languageForMode(mode), autocompletion()]
}

/*
Runtime-introspection hooks for tjs autocomplete (all optional).

Declared here rather than imported from `tjs-lang/editors/codemirror`, even though tjs-lang
0.10.0+ ships this type (tjs-lang#12) and we did import it. `import type` is erased at build
— but it is NOT erased from the emitted `.d.ts`, and `tjs-lang` is an OPTIONAL peer. So the
import made a package we tell people they do not need into one their `tsc` cannot run
without: a consumer whose only import is `tosiCode` got TS2307 for a language toolchain they
never asked for. Declaring an optional peer does not help — TypeScript does not care what npm
thinks is optional.

Structural, so it stays interchangeable with theirs at the boundary; `code-editor-cm.test.ts`
asserts assignability in both directions against the real type, which is a devDependency.
Reported upstream as tjs-lang#13 for the benefit of their other consumers; the fix here is
ours either way. Same reasoning, same shape, as `src/schema-form/json-schema.ts`.
*/
export interface TjsIntrospectMember {
  label: string
  /** 'method' for callables, else 'property'. */
  type: 'method' | 'property'
  /** typeof for properties, an arg hint for methods. */
  detail: string
}

export interface TjsAutocompleteConfig {
  /** get `__tjs` metadata from the current source */
  getMetadata?: () => Record<string, any> | undefined
  /** get imported-module metadata */
  getImports?: () => Record<string, Record<string, any>> | undefined
  /** live module exports, for runtime introspection */
  getLiveBindings?: () => Record<string, any> | undefined
  /** async member completion from the introspection bridge */
  getMembers?: (path: string) => Promise<TjsIntrospectMember[] | undefined>
}

/**
 * Lazy-load tjs-lang's CodeMirror language + completion bundle. Returns a single
 * CodeMirror `Extension` (tjs language, forbidden-keyword highlighting, theme, and
 * `tjsCompletionSource`-driven autocomplete) suitable for `setLanguageExtension()`,
 * or `null` when tjs-lang (an optional peer) isn't installed or its editor build
 * lacks `tjsEditorExtension`. The dynamic import keeps it out of the bundle until a
 * tjs editor actually mounts.
 */
export async function loadTjsExtension(
  autocomplete: TjsAutocompleteConfig = {}
): Promise<Extension | null> {
  try {
    const mod = (await import('tjs-lang/editors/codemirror')) as {
      tjsEditorExtension?: (config: {
        typescript?: boolean
        jsx?: boolean
        autocomplete?: TjsAutocompleteConfig
      }) => Extension
    }
    if (typeof mod.tjsEditorExtension === 'function') {
      return mod.tjsEditorExtension({ typescript: true, autocomplete })
    }
  } catch {
    // tjs-lang absent or editor build unavailable — caller keeps TS highlighting.
  }
  return null
}

// A minimal theme so the editor has a real surface instead of showing whatever is
// behind it. It's applied as a CodeMirror theme (not shadow-DOM CSS) so it beats
// CM's injected base styles. Colors come from the doc-system theme vars
// (`--code-bg` / `--text-color`, which pierce the shadow root and adapt to dark
// mode) with sensible standalone fallbacks.
const editorTheme = EditorView.theme({
  '&': {
    height: '100%',
    backgroundColor: 'var(--code-bg, var(--input-bg, #fdfdfd))',
    color: 'var(--text-color, #222)',
  },
  '.cm-scroller': {
    fontFamily: "Menlo, Monaco, Consolas, 'Courier New', monospace",
  },
  '.cm-gutters': {
    backgroundColor: 'var(--code-bg, var(--input-bg, #fdfdfd))',
    color: 'var(--text-color, #888)',
    border: 'none',
  },
  '.cm-activeLine': { backgroundColor: 'transparent' },
  '.cm-activeLineGutter': { backgroundColor: 'transparent' },
})

// A basicSetup-equivalent without a hard-coded color theme, so the host page's CSS
// (and dark mode) controls the palette. Line-wrapping mirrors the old ACE `wrap:true`.
function baseExtensions(): Extension {
  return [
    editorTheme,
    // Render autocomplete / hover tooltips in a fixed layer so they escape the
    // live-example's `:host { overflow: hidden }` (the rounded card clip) instead
    // of being cut off at the editor's bottom edge in a short example.
    tooltips({ position: 'fixed' }),
    lineNumbers(),
    highlightActiveLineGutter(),
    highlightSpecialChars(),
    history(),
    foldGutter(),
    drawSelection(),
    dropCursor(),
    EditorState.allowMultipleSelections.of(true),
    indentOnInput(),
    indentUnit.of('  '),
    EditorState.tabSize.of(2),
    // syntax highlighting is applied per-instance via a compartment (dark-mode aware)
    bracketMatching(),
    closeBrackets(),
    // NB: autocompletion() lives in the language compartment (see
    // standardLanguageBundle) so tjs's own completion source never doubles up.
    rectangularSelection(),
    crosshairCursor(),
    highlightActiveLine(),
    highlightSelectionMatches(),
    EditorView.lineWrapping,
    keymap.of([
      indentWithTab,
      ...closeBracketsKeymap,
      ...defaultKeymap,
      ...searchKeymap,
      ...historyKeymap,
      ...foldKeymap,
      ...completionKeymap,
      ...lintKeymap,
    ]),
  ]
}

export interface CmHandle {
  view: EditorView
  getValue(): string
  setValue(text: string): void
  setMode(mode: string): void
  /** Swap the language compartment wholesale (e.g. an async-loaded tjs bundle). */
  setLanguageExtension(ext: Extension): void
  setReadOnly(readOnly: boolean): void
  undo(): void
  redo(): void
  canUndo(): boolean
  canRedo(): boolean
  focus(): void
  refresh(): void
  destroy(): void
}

export interface CmOptions {
  value?: string
  mode?: string
  readOnly?: boolean
  /** additional language-adjacent extensions (e.g. a tjs language + autocomplete) */
  languageExtension?: Extension
  onChange?: (value: string) => void
  /** style-injection root — pass the host's ShadowRoot when mounted in shadow DOM */
  root?: Document | ShadowRoot
  /** the editable area's accessible name */
  label?: string
}

// ONE darkmode observer for the whole module, with a registry of live editors —
// not one MutationObserver per editor. A registered observer is strongly held by the
// node it observes, so a per-editor `observe(document.body)` pins document.body →
// observer → closure → EditorView, and the editor survives detachment forever. A doc
// page mounts ~20 editors, so every SPA navigation used to leak all of them (and each
// darkmode toggle then dispatched into every editor ever created).
// Same pattern as localize.ts / drag-and-drop.ts.
type DarkListener = () => void
const darkListeners = new Set<DarkListener>()
let darkObserver: MutationObserver | undefined

function watchDarkMode(listener: DarkListener): () => void {
  darkListeners.add(listener)
  if (
    darkObserver === undefined &&
    typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    document.body
  ) {
    darkObserver = new MutationObserver(() => {
      for (const l of darkListeners) l()
    })
    darkObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    })
  }
  return () => {
    darkListeners.delete(listener)
    if (darkListeners.size === 0) {
      darkObserver?.disconnect()
      darkObserver = undefined
    }
  }
}

/** Create a CodeMirror editor mounted in `parent`, returning an editor-agnostic handle. */
export function createCmEditor(
  parent: HTMLElement,
  opts: CmOptions = {}
): CmHandle {
  const language = new Compartment()
  const readonly = new Compartment()
  const highlight = new Compartment()

  const langExt =
    opts.languageExtension ?? standardLanguageBundle(opts.mode ?? 'javascript')

  let view: EditorView
  try {
    view = new EditorView({
      parent,
      root: opts.root,
      state: EditorState.create({
        doc: opts.value ?? '',
        extensions: [
          baseExtensions(),
          highlight.of(highlightFor(isDarkMode())),
          language.of(langExt),
          readonly.of(EditorState.readOnly.of(!!opts.readOnly)),
          EditorView.contentAttributes.of({
            'aria-label': opts.label || 'code editor',
          }),
          EditorView.updateListener.of((u) => {
            if (!u.docChanged || !opts.onChange) return
            // A programmatic `value` set is not a change the USER made. See `programmatic`.
            if (u.transactions.some((t) => t.annotation(programmatic))) return
            opts.onChange(u.state.doc.toString())
          }),
        ],
      }),
    })
  } catch (error) {
    /*
    CodeMirror recognises extensions by identity, so a second copy of `@codemirror/state`
    in the install makes it reject its own language packages. The editor is then blank and
    the message names an internal; say what happened and what fixes it (board #3248).
    */
    if (
      /Unrecognized extension value/.test(String((error as Error)?.message))
    ) {
      console.error(
        '<tosi-code>: CodeMirror rejected an extension, which means two copies of ' +
          '@codemirror/state (or @codemirror/view) are loaded. Every CodeMirror package ' +
          'must share one. Run `bun update @codemirror/state @codemirror/view` (or ' +
          '`npm dedupe`), then rebuild.'
      )
    }
    throw error
  }

  // Re-apply the right highlight palette when the doc-system toggles dark mode
  // (it flips the `darkmode` class on <body>).
  let dark = isDarkMode()
  const unwatchDark = watchDarkMode(() => {
    const nowDark = isDarkMode()
    if (nowDark !== dark) {
      dark = nowDark
      view.dispatch({ effects: highlight.reconfigure(highlightFor(dark)) })
    }
  })

  const handle: CmHandle = {
    view,
    getValue: () => view.state.doc.toString(),
    setValue(text: string) {
      if (view.state.doc.toString() === text) return
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: text },
        annotations: [
          programmatic.of(true),
          // Loading a document is not an edit to undo back out of. (The ACE wrapper
          // called `getUndoManager().reset()` at this point, for the same reason.)
          Transaction.addToHistory.of(false),
        ],
      })
    },
    setMode(mode: string) {
      view.dispatch({
        effects: language.reconfigure(standardLanguageBundle(mode)),
      })
    },
    setLanguageExtension(ext: Extension) {
      view.dispatch({ effects: language.reconfigure(ext) })
    },
    setReadOnly(ro: boolean) {
      view.dispatch({
        effects: readonly.reconfigure(EditorState.readOnly.of(ro)),
      })
    },
    undo: () => void undo(view),
    redo: () => void redo(view),
    canUndo: () => undoDepth(view.state) > 0,
    canRedo: () => redoDepth(view.state) > 0,
    focus: () => view.focus(),
    refresh: () => view.requestMeasure(),
    destroy: () => {
      unwatchDark()
      view.destroy()
    },
  }
  return handle
}
